import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, Connection, Conversation, Label, InboxStatusCount } from '../api/client'
import { useWorkspaceContext } from '../hooks/useWorkspaceContext'

const STATUS_TABS = ['All', 'New', 'Open', 'Pending', 'Resolved'] as const
const TYPE_OPTIONS = ['All', 'Comment', 'DM', 'Mention', 'Review'] as const

const PUSH_PLATFORMS = new Set(['Facebook', 'Threads', 'Bluesky'])
const METERED_PLATFORMS = new Set(['X/Twitter'])
const TIER2_PLATFORMS = new Set(['YouTube', 'LinkedIn', 'TikTok', 'Instagram'])

function extractHandle(displayName?: string): string | null {
  if (!displayName) return null
  const parenMatch = displayName.match(/\(@?([\w.]+)\)/)
  if (parenMatch) return parenMatch[1]
  const atMatches = [...displayName.matchAll(/@([\w.]+)/g)]
  if (atMatches.length > 0) return atMatches[atMatches.length - 1][1]
  return null
}

function getProfileUrl(c: Connection): string | null {
  const p = c.platformName
  const id = c.platformAccountID
  if (!p || !id) return null
  if (p === 'Facebook') return `https://www.facebook.com/${id}`
  const handle = extractHandle(c.displayName)
  if (p === 'Instagram') return `https://www.instagram.com/${handle || id}/`
  if (p === 'Threads') return `https://www.threads.net/@${handle || id}`
  if (p === 'Bluesky') return `https://bsky.app/profile/${id}`
  if (p === 'X/Twitter') return `https://x.com/${handle || id}`
  if (p === 'YouTube') return `https://www.youtube.com/channel/${id}`
  if (p.includes('LinkedIn') && id.includes('urn:li:organization:'))
    return `https://www.linkedin.com/company/${id.split(':').pop()}/`
  if (p.includes('LinkedIn') && id.includes('urn:li:person:'))
    return `https://www.linkedin.com/in/me/`
  if (p === 'Pinterest') return `https://www.pinterest.com/${id}/`
  if (p === 'TikTok') return `https://www.tiktok.com/@${c.displayName || id}`
  return null
}
type XSettings = { enabled: boolean; monthlyBudgetUsd: string; monthlySpentUsd: string }

function getSyncTier(platform: string | undefined) {
  if (!platform) return 'unavailable'
  if (PUSH_PLATFORMS.has(platform)) return 'live'
  if (METERED_PLATFORMS.has(platform)) return 'metered'
  if (TIER2_PLATFORMS.has(platform)) return 'tier2'
  return 'unavailable'
}

function getTokenHealth(c: Connection): 'healthy' | 'expired' | 'error' | 'paused' {
  if (c.isPaused) return 'paused'
  if (c.lastTokenError) return 'error'
  return 'healthy'
}

function timeAgo(dateStr: string | undefined) {
  if (!dateStr) return 'Never synced'
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

export default function Inbox() {
  const navigate = useNavigate()
  const { activeWorkspace } = useWorkspaceContext()
  const wsId = activeWorkspace?.workspaceID ?? 0
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [connections, setConnections] = useState<Connection[]>([])
  const [selected, setSelected] = useState<Conversation | null>(null)
  const [statusFilter, setStatusFilter] = useState('All')
  const [typeFilter, setTypeFilter] = useState('All')
  const [platformFilters, setPlatformFilters] = useState<Set<string> | null>(null)
  const [tiktokGateModal, setTiktokGateModal] = useState(false)
  const [labels, setLabels] = useState<Label[]>([])
  const [counts, setCounts] = useState<InboxStatusCount[]>([])
  const [replyText, setReplyText] = useState('')
  const [noteText, setNoteText] = useState('')
  const [loading, setLoading] = useState(true)
  const [syncingId, setSyncingId] = useState<number | null>(null)
  const [syncingAll, setSyncingAll] = useState(false)
  const [syncCooldown, setSyncCooldown] = useState(() => {
    const stored = sessionStorage.getItem('smm_sync_cooldown_expires')
    if (stored) {
      const remaining = Math.ceil((parseInt(stored, 10) - Date.now()) / 1000)
      return remaining > 0 ? remaining : 0
    }
    return 0
  })

  const startSyncCooldown = useCallback((seconds: number) => {
    setSyncCooldown(seconds)
    sessionStorage.setItem('smm_sync_cooldown_expires', String(Date.now() + seconds * 1000))
  }, [])
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [statusDropOpen, setStatusDropOpen] = useState(false)
  const [typeDropOpen, setTypeDropOpen] = useState(false)
  const [xSettings, setXSettings] = useState<XSettings | null>(null)
  const [showXSettings, setShowXSettings] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const statusDropRef = useRef<HTMLDivElement>(null)
  const typeDropRef = useRef<HTMLDivElement>(null)
  const [origPost, setOrigPost] = useState<{ preview?: string; imageUrl?: string; url?: string; postId?: string } | null>(null)
  const [origPostLoading, setOrigPostLoading] = useState(false)
  const [origPostModal, setOrigPostModal] = useState(false)
  const [mobileShowDetail, setMobileShowDetail] = useState(false)
  const [reauthConnections, setReauthConnections] = useState<{ connectionID: number; platformName: string; displayName: string; lastTokenError?: string }[]>([])
  const [showReauthModal, setShowReauthModal] = useState(false)

  const fetchConversations = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (statusFilter !== 'All') params.set('status', statusFilter)
      if (typeFilter !== 'All') params.set('type', typeFilter)
      const qs = params.toString() ? `?${params}` : ''
      const data = await api.get<Conversation[]>(`/workspaces/${wsId}/inbox/conversations${qs}`)
      setConversations(data)
    } catch (err) {
      console.error('[SMM] fetch conversations failed', err)
    }
    setLoading(false)
  }, [wsId, statusFilter, typeFilter])

  useEffect(() => { fetchConversations() }, [fetchConversations])

  useEffect(() => {
    const runLoginSync = async () => {
      try {
        await api.post(`/workspaces/${wsId}/inbox/reconcile`, {})
        startSyncCooldown(120)
        setTimeout(() => fetchConversations(), 5000)
      } catch {
        console.error('[SMM] login reconcile failed')
      }
    }
    runLoginSync()

    api.get<{ connectionID: number; platformName: string; displayName: string; needsReauth: boolean; lastTokenError?: string }[]>(
      `/workspaces/${wsId}/inbox/connection-health`
    ).then(items => {
      const needsReauth = items.filter(i => i.needsReauth)
      if (needsReauth.length > 0) {
        setReauthConnections(needsReauth)
        setShowReauthModal(true)
      }
    }).catch(() => {})
  }, [wsId])

  useEffect(() => {
    if (syncCooldown <= 0) return
    const interval = setInterval(() => {
      setSyncCooldown(prev => {
        if (prev <= 1) { clearInterval(interval); return 0 }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [syncCooldown])

  useEffect(() => {
    api.get<Label[]>(`/workspaces/${wsId}/inbox/labels`).then(setLabels).catch(err => console.error("[SMM]", err))
    api.get<InboxStatusCount[]>(`/workspaces/${wsId}/inbox/counts`).then(setCounts).catch(err => console.error("[SMM]", err))
    api.get<Connection[]>(`/workspaces/${wsId}/connections`).then(setConnections).catch(err => console.error('[SMM]', err))
    api.get<XSettings>(`/workspaces/${wsId}/inbox/settings/x`).then(setXSettings).catch(err => console.error('[SMM]', err))
  }, [wsId])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node))
        setDropdownOpen(false)
      if (statusDropRef.current && !statusDropRef.current.contains(e.target as Node))
        setStatusDropOpen(false)
      if (typeDropRef.current && !typeDropRef.current.contains(e.target as Node))
        setTypeDropOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    setOrigPost(null)
    setOrigPostModal(false)
    if (!selected?.originalPostID) return
    if (selected.originalPostPreview) {
      setOrigPost({ preview: selected.originalPostPreview, url: selected.originalPostUrl ?? undefined, postId: selected.originalPostID })
      return
    }
    setOrigPostLoading(true)
    api.get<{ preview?: string; imageUrl?: string; url?: string; postId?: string }>(
      `/workspaces/${wsId}/inbox/conversations/${selected.conversationID}/original-post`
    ).then(data => setOrigPost(data))
      .catch(err => console.error('[SMM] fetch original post failed', err))
      .finally(() => setOrigPostLoading(false))
  }, [selected?.conversationID, selected?.originalPostID, selected?.originalPostPreview, selected?.originalPostUrl, wsId])

  const platforms = useMemo(() =>
    [...new Set(connections.map(c => c.platformName).filter((p): p is string => !!p))],
    [connections])

  const togglePlatform = (p: string) => {
    if (p === 'TikTok') {
      const tikTokConn = connections.find(c => c.platformName === 'TikTok' && c.isActive)
      if (tikTokConn && tikTokConn.accountType !== 'Business') {
        const current = platformFilters ?? new Set(platforms)
        if (!current.has('TikTok')) {
          setTiktokGateModal(true)
          return
        }
      }
    }
    setPlatformFilters(prev => {
      const current = prev ?? new Set(platforms)
      const n = new Set(current)
      if (n.has(p)) n.delete(p); else n.add(p)
      return n
    })
  }

  const selectAllPlatforms = () => {
    const allSelected = platformFilters === null || platformFilters.size === platforms.length
    if (allSelected) setPlatformFilters(new Set())
    else setPlatformFilters(null)
  }

  const handleSyncConnection = async (connectionId: number) => {
    setSyncingId(connectionId)
    try {
      await api.post(`/workspaces/${wsId}/inbox/sync/${connectionId}`, {})
      await fetchConversations()
      const newCounts = await api.get<InboxStatusCount[]>(`/workspaces/${wsId}/inbox/counts`)
      setCounts(newCounts)
      const updatedConns = await api.get<Connection[]>(`/workspaces/${wsId}/connections`)
      setConnections(updatedConns)
    } catch (err) {
      console.error('[SMM] sync failed', err)
    }
    setSyncingId(null)
  }

  const handleSyncAll = async () => {
    if (syncCooldown > 0) return
    setSyncingAll(true)
    try {
      const res = await api.post<{ rateLimited?: boolean; message?: string }>(`/workspaces/${wsId}/inbox/sync-all`, {})
      if (res.rateLimited) {
        startSyncCooldown(120)
      } else {
        startSyncCooldown(120)
        setTimeout(async () => {
          try {
            await fetchConversations()
            const newCounts = await api.get<InboxStatusCount[]>(`/workspaces/${wsId}/inbox/counts`)
            setCounts(newCounts)
            const updatedConns = await api.get<Connection[]>(`/workspaces/${wsId}/connections`)
            setConnections(updatedConns)
          } catch { console.error('[SMM] post-sync refetch failed') }
        }, 8000)
      }
    } catch (err) {
      console.error('[SMM] sync all failed', err)
    }
    setSyncingAll(false)
  }

  const handleToggleX = async () => {
    if (!xSettings) return
    const newEnabled = !xSettings.enabled
    try {
      await api.put(`/workspaces/${wsId}/inbox/settings/x`, { enabled: newEnabled })
      setXSettings({ ...xSettings, enabled: newEnabled })
    } catch (err) {
      console.error('[SMM] X toggle failed', err)
    }
  }

  const handleXBudget = async (budget: string) => {
    if (!xSettings) return
    const val = parseFloat(budget)
    if (isNaN(val) || val < 0) return
    try {
      await api.put(`/workspaces/${wsId}/inbox/settings/x`, { enabled: xSettings.enabled, monthlyBudgetUsd: val })
      setXSettings({ ...xSettings, monthlyBudgetUsd: val.toFixed(2) })
    } catch (err) {
      console.error('[SMM] X budget update failed', err)
    }
  }

  const handleToggleXMonitor = async (c: Connection) => {
    try {
      if (c.monitorsActivity) {
        await api.del(`/workspaces/${wsId}/inbox/connections/${c.connectionID}/x-monitor`)
      } else {
        await api.post(`/workspaces/${wsId}/inbox/connections/${c.connectionID}/x-monitor`, {})
      }
      const updatedConns = await api.get<Connection[]>(`/workspaces/${wsId}/connections`)
      setConnections(updatedConns)
    } catch (err) {
      console.error('[SMM] X monitor toggle failed', err)
    }
  }

  const selectConversation = async (id: number) => {
    try {
      const conv = await api.get<Conversation>(`/workspaces/${wsId}/inbox/conversations/${id}`)
      setSelected(conv)
      setMobileShowDetail(true)
      setReplyText('')
      setNoteText('')
      setReplyError(null)
      setMsgPage(1)
      setSentReactions({})
      const initialHidden = new Set<string>()
      conv.messages?.forEach((m: any) => { if (m.isHidden && m.platformMessageID) initialHidden.add(m.platformMessageID) })
      setHiddenMsgIds(initialHidden)
      setDeletedMsgIds(new Set())

      if (conv.conversationType === 'Comment') {
        api.post(`/workspaces/${wsId}/inbox/conversations/${id}/refresh-likes`, {})
          .then(async (res: any) => {
            if (res?.updated > 0) {
              const refreshed = await api.get<Conversation>(`/workspaces/${wsId}/inbox/conversations/${id}`)
              setSelected(refreshed)
            }
          })
          .catch((err: unknown) => console.error('[SMM] refresh likes failed', err))
      }
    } catch (err) {
      console.error('[SMM] select conversation failed', err)
    }
  }

  const [replyError, setReplyError] = useState<string | null>(null)
  const [replying, setReplying] = useState(false)
  const [msgPage, setMsgPage] = useState(1)
  const MSG_PAGE_SIZE = 20

  const handleReply = async () => {
    if (!selected || !replyText.trim()) return
    setReplying(true)
    setReplyError(null)
    try {
      await api.post(`/workspaces/${wsId}/inbox/conversations/${selected.conversationID}/reply`,
        { content: replyText })
      setReplyText('')
      await selectConversation(selected.conversationID)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Reply failed'
      setReplyError(msg)
      console.error('[SMM] reply failed', err)
    } finally {
      setReplying(false)
    }
  }

  const handleNote = async () => {
    if (!selected || !noteText.trim()) return
    try {
      await api.post(`/workspaces/${wsId}/inbox/conversations/${selected.conversationID}/note`,
        { content: noteText })
      setNoteText('')
      await selectConversation(selected.conversationID)
    } catch (err) {
      console.error('[SMM] note failed', err)
    }
  }

  const handleStatusChange = async (status: string) => {
    if (!selected) return
    try {
      await api.put(`/workspaces/${wsId}/inbox/conversations/${selected.conversationID}/status`, { status })
      setSelected({ ...selected, status })
      fetchConversations()
    } catch (err) {
      console.error('[SMM] status change failed', err)
    }
  }

  const handleArchive = async () => {
    if (!selected) return
    try {
      await api.put(`/workspaces/${wsId}/inbox/conversations/${selected.conversationID}/archive?archived=${!selected.isArchived}`)
      setSelected(null)
      fetchConversations()
    } catch (err) {
      console.error('[SMM] archive failed', err)
    }
  }

  const handleAddLabel = async (labelId: number) => {
    if (!selected) return
    try {
      await api.post(`/workspaces/${wsId}/inbox/conversations/${selected.conversationID}/labels/${labelId}`)
      await selectConversation(selected.conversationID)
    } catch (err) {
      console.error('[SMM] add label failed', err)
    }
  }

  const handleRemoveLabel = async (labelId: number) => {
    if (!selected) return
    try {
      await api.del(`/workspaces/${wsId}/inbox/conversations/${selected.conversationID}/labels/${labelId}`)
      await selectConversation(selected.conversationID)
    } catch (err) {
      console.error('[SMM] remove label failed', err)
    }
  }

  const [hiddenMsgIds, setHiddenMsgIds] = useState<Set<string>>(new Set())
  const [deletedMsgIds, setDeletedMsgIds] = useState<Set<string>>(new Set())

  const handleDeleteComment = async (platformMessageId: string) => {
    if (!selected || !confirm('Delete this comment permanently? This cannot be undone.')) return
    try {
      await api.del(`/workspaces/${wsId}/inbox/comments/${encodeURIComponent(platformMessageId)}?connectionId=${selected.connectionID}`)
      setDeletedMsgIds(prev => new Set([...prev, platformMessageId]))
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Delete failed'
      console.error('[SMM] delete comment failed', err)
      alert(msg)
    }
  }

  const handleHideComment = async (platformMessageId: string) => {
    if (!selected) return
    const isCurrentlyHidden = hiddenMsgIds.has(platformMessageId)
    try {
      await api.post(`/workspaces/${wsId}/inbox/comments/${encodeURIComponent(platformMessageId)}/hide?connectionId=${selected.connectionID}`, { hide: !isCurrentlyHidden })
      setHiddenMsgIds(prev => {
        const next = new Set(prev)
        if (isCurrentlyHidden) next.delete(platformMessageId)
        else next.add(platformMessageId)
        return next
      })
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Hide failed'
      if (msg.includes('1446036') || msg.includes('already marked as spam')) {
        setHiddenMsgIds(prev => new Set([...prev, platformMessageId]))
        return
      }
      console.error('[SMM] hide comment failed', err)
      alert(msg)
    }
  }

  const handleModerate = async (platformMessageId: string, action: 'flag' | 'reject' | 'delete' | 'publish') => {
    if (!selected) return
    try {
      await api.post(`/workspaces/${wsId}/inbox/comments/${encodeURIComponent(platformMessageId)}/moderate?connectionId=${selected.connectionID}`, { action })
      if (action === 'delete') {
        await fetchConversations()
      } else {
        setHiddenMsgIds(prev => {
          const next = new Set(prev)
          if (action === 'publish') next.delete(platformMessageId)
          else next.add(platformMessageId)
          return next
        })
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Moderation failed'
      console.error('[SMM] moderate comment failed', err)
      alert(msg)
    }
  }

  const handleDisableComments = async (disable: boolean) => {
    if (!selected?.originalPostID) return
    try {
      await api.post(`/workspaces/${wsId}/inbox/posts/${encodeURIComponent(selected.originalPostID)}/disable-comments?connectionId=${selected.connectionID}`, { disable })
      alert(disable ? 'Comments disabled on this post' : 'Comments re-enabled on this post')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed'
      console.error('[SMM] disable comments failed', err)
      alert(msg)
    }
  }

  const [reactPopupMsgId, setReactPopupMsgId] = useState<string | null>(null)
  const [sentReactions, setSentReactions] = useState<Record<string, string>>({})
  const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢']
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [editingMsgId, setEditingMsgId] = useState<string | null>(null)
  const [editText, setEditText] = useState('')

  useEffect(() => {
    if (selected && selected.conversationType === 'DM') {
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'auto' }), 50)
    }
  }, [selected?.conversationID])

  const handleReactToMessage = async (platformMessageId: string, reaction: string) => {
    if (!selected) return
    setReactPopupMsgId(null)
    try {
      let recipientId: string | undefined
      const threadId = selected.platformThreadID
      if (threadId?.startsWith('dm:')) {
        const parts = threadId.split(':')
        if (parts.length >= 3) recipientId = parts[2]
      }
      setSentReactions(prev => ({ ...prev, [platformMessageId]: reaction }))
      await api.post(`/workspaces/${wsId}/inbox/messages/${encodeURIComponent(platformMessageId)}/react?connectionId=${selected.connectionID}`, { reaction, recipientId })
    } catch (err) {
      console.error('[SMM] react failed', err)
    }
  }

  const handleUnreactToMessage = async (platformMessageId: string) => {
    if (!selected) return
    setReactPopupMsgId(null)
    setSentReactions(prev => ({ ...prev, [platformMessageId]: '' }))
    try {
      let recipientId: string | undefined
      const threadId = selected.platformThreadID
      if (threadId?.startsWith('dm:')) {
        const parts = threadId.split(':')
        if (parts.length >= 3) recipientId = parts[2]
      }
      await api.post(`/workspaces/${wsId}/inbox/messages/${encodeURIComponent(platformMessageId)}/react?connectionId=${selected.connectionID}`, { reaction: '', recipientId, action: 'unreact' })
    } catch (err) {
      console.error('[SMM] unreact failed', err)
    }
  }

  const handleEditMessage = async (platformMessageId: string) => {
    if (!selected || !editText.trim()) return
    try {
      await api.put(`/workspaces/${wsId}/inbox/messages/${encodeURIComponent(platformMessageId)}/edit?connectionId=${selected.connectionID}`, { text: editText.trim() })
      const refreshed = await api.get<Conversation>(`/workspaces/${wsId}/inbox/conversations/${selected.conversationID}`)
      setSelected(refreshed)
      setEditingMsgId(null)
      setEditText('')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Edit failed'
      console.error('[SMM] edit message failed', err)
      alert(msg)
    }
  }

  const getCount = (status: string) => counts.find(c => c.status === status)?.total ?? 0

  const platformLabel = platformFilters === null || platformFilters.size === platforms.length ? 'All Networks'
    : platformFilters.size === 0 ? 'None selected'
    : `${platformFilters.size} Network${platformFilters.size > 1 ? 's' : ''}`

  const [viewMode, setViewMode] = useState<'conversations' | 'byPost'>('conversations')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [sortBy, setSortBy] = useState<'newest' | 'reactions' | 'postLikes'>('newest')
  const [listPageSize, setListPageSize] = useState(25)
  const [listPage, setListPage] = useState(1)

  const allFiltered = useMemo(() => {
    let result = conversations.filter(c =>
      platformFilters === null || platformFilters.has(c.platformName || ''))
    if (dateFrom) {
      const fromTs = new Date(dateFrom).getTime()
      result = result.filter(c => c.lastMessageAt && new Date(c.lastMessageAt).getTime() >= fromTs)
    }
    if (dateTo) {
      const toTs = new Date(dateTo + 'T23:59:59').getTime()
      result = result.filter(c => c.lastMessageAt && new Date(c.lastMessageAt).getTime() <= toTs)
    }
    if (sortBy === 'reactions') {
      result = [...result].sort((a, b) => (b.totalLikes ?? 0) - (a.totalLikes ?? 0))
    } else if (sortBy === 'postLikes') {
      result = [...result].sort((a, b) => (b.postLikes ?? 0) - (a.postLikes ?? 0))
    }
    return result
  }, [conversations, platformFilters, dateFrom, dateTo, sortBy])
  const totalPages = Math.max(1, Math.ceil(allFiltered.length / listPageSize))
  const filtered = allFiltered.slice((listPage - 1) * listPageSize, listPage * listPageSize)

  return (
    <div className="smm-inbox">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h1>Inbox</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {xSettings && (
            <button
              onClick={() => setShowXSettings(!showXSettings)}
              className="smm-btn smm-btn-sm"
              style={{ fontSize: '0.8rem', background: xSettings.enabled ? '#e8f5e9' : '#fafafa', border: '1px solid #ccc', borderRadius: 6 }}>
              X Inbox: ${xSettings.monthlySpentUsd}/{xSettings.monthlyBudgetUsd}
              <span style={{ marginLeft: 6, fontSize: 10, color: xSettings.enabled ? '#2e7d32' : '#999' }}>
                {xSettings.enabled ? 'ON' : 'OFF'}
              </span>
            </button>
          )}
          <button
            className="smm-btn smm-btn-sm"
            style={{ fontSize: '0.75rem', background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 6 }}
            onClick={() => navigate('/webhook-events')}>
            Webhook Events
          </button>
        </div>
      </div>

      {showXSettings && xSettings && (
        <div style={{
          background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 8,
          padding: '12px 16px', margin: '8px 0', display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap'
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' }}>
            <input type="checkbox" checked={xSettings.enabled} onChange={handleToggleX} />
            X/Twitter webhook active
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' }}>
            Monthly budget: $
            <input type="number" step="5" min="0" value={xSettings.monthlyBudgetUsd}
              onChange={e => handleXBudget(e.target.value)}
              style={{ width: 70, padding: '2px 6px', borderRadius: 4, border: '1px solid #ccc' }} />
          </label>
          <span style={{ fontSize: '0.8rem', color: '#666' }}>
            Spent this month: ${xSettings.monthlySpentUsd}
            {parseFloat(xSettings.monthlySpentUsd) >= parseFloat(xSettings.monthlyBudgetUsd) &&
              <span style={{ color: '#d32f2f', fontWeight: 600, marginLeft: 8 }}>Budget reached -- monitoring paused</span>}
          </span>
          <div style={{ width: '100%', borderTop: '1px solid #e0e0e0', paddingTop: 8, marginTop: 4 }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#333' }}>Activity Monitoring (per connection):</span>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 6 }}>
              {connections.filter(c => c.platformName === 'X/Twitter').map(c => (
                <label key={c.connectionID} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', padding: '3px 8px', borderRadius: 4, background: c.monitorsActivity ? '#e8f5e9' : '#fff', border: '1px solid #ccc' }}>
                  <input type="checkbox" checked={!!c.monitorsActivity} onChange={() => handleToggleXMonitor(c)} />
                  {c.displayName}
                  <span style={{ fontSize: '0.7rem', color: c.monitorsActivity ? '#2e7d32' : '#999' }}>
                    {c.monitorsActivity ? 'Streaming' : 'Post-only'}
                  </span>
                </label>
              ))}
              {connections.filter(c => c.platformName === 'X/Twitter').length === 0 &&
                <span style={{ fontSize: '0.8rem', color: '#999' }}>No X/Twitter connections</span>}
            </div>
          </div>
        </div>
      )}

      <div style={{
        display: 'flex', gap: 6, flexWrap: 'wrap', margin: '8px 0', fontSize: '0.78rem'
      }}>
        {connections.map(c => {
          const tier = getSyncTier(c.platformName)
          const health = getTokenHealth(c)
          const isUnhealthy = health === 'expired' || health === 'error'
          return (
            <div key={c.connectionID} style={{
              display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px',
              background: isUnhealthy ? '#fff3e0' : '#f8f9fa', borderRadius: 12,
              border: `1px solid ${isUnhealthy ? '#ff9800' : '#eee'}`
            }}>
              {(() => {
                const url = getProfileUrl(c)
                return url ? (
                  <a href={url} target="_blank" rel="noopener noreferrer"
                    style={{ fontWeight: 500, color: 'inherit', textDecoration: 'none' }}
                    title={`Open ${c.displayName || c.platformName} profile`}>
                    {c.platformName}
                  </a>
                ) : (
                  <span style={{ fontWeight: 500 }}>{c.platformName}</span>
                )
              })()}
              {isUnhealthy ? (
                <button
                  onClick={() => navigate(`/connections?reconnect=${encodeURIComponent(c.platformName || '')}`)}
                  style={{
                    color: '#d32f2f', fontSize: '0.72rem', fontWeight: 600,
                    textDecoration: 'underline', cursor: 'pointer',
                    background: 'none', border: 'none', padding: 0
                  }}
                  title={c.lastTokenError || 'Token expired -- click to reconnect'}
                >
                  Reconnect
                </button>
              ) : (
                <>
                  {tier === 'live' && (
                    <span style={{ color: '#2e7d32', fontSize: '0.72rem', fontWeight: 600 }}>Live</span>
                  )}
                  {tier === 'metered' && (
                    <span style={{ color: '#e65100', fontSize: '0.72rem', fontWeight: 600 }}>
                      Live (metered)
                    </span>
                  )}
                  {tier === 'tier2' && (
                    <>
                      <span style={{ color: '#555', fontSize: '0.72rem' }}>{timeAgo(c.lastCommentSyncAt)}</span>
                      <button
                        onClick={() => handleSyncConnection(c.connectionID)}
                        disabled={syncingId === c.connectionID}
                        style={{
                          fontSize: '0.7rem', padding: '1px 6px', borderRadius: 4,
                          border: '1px solid #1976d2', background: '#e3f2fd', color: '#1565c0',
                          cursor: 'pointer'
                        }}>
                        {syncingId === c.connectionID ? '...' : 'Sync'}
                      </button>
                    </>
                  )}
                  {tier === 'unavailable' && (() => {
                    const url = getProfileUrl(c)
                    return url ? (
                      <a href={url} target="_blank" rel="noopener noreferrer"
                        style={{ color: '#1976d2', fontSize: '0.72rem', textDecoration: 'none' }}
                        title="Open profile (new tab)">
                        Profile ↗
                      </a>
                    ) : (
                      <span style={{ color: '#999', fontSize: '0.72rem' }}>N/A</span>
                    )
                  })()}
                </>
              )}
            </div>
          )
        })}
        <button
          onClick={handleSyncAll}
          disabled={syncingAll || syncCooldown > 0}
          style={{
            fontSize: '0.78rem', padding: '3px 10px', borderRadius: 6,
            border: '1px solid #388e3c', background: (syncingAll || syncCooldown > 0) ? '#c8e6c9' : '#e8f5e9',
            color: '#2e7d32', cursor: (syncingAll || syncCooldown > 0) ? 'not-allowed' : 'pointer',
            fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4
          }}>
          {syncingAll ? 'Syncing...' : syncCooldown > 0 ? `Sync All (${syncCooldown}s)` : 'Sync All'}
        </button>
      </div>

      <div className="smm-inbox-filters">
        <div className="smm-filter-bar" style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="smm-btn smm-btn-sm"
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: '#fff', border: '1px solid #ccc', borderRadius: 6,
                padding: '4px 12px', cursor: 'pointer', fontSize: '0.85rem',
                minWidth: 140
              }}>
              <span>{platformLabel}</span>
              <span style={{ fontSize: 10 }}>{dropdownOpen ? '\u25B2' : '\u25BC'}</span>
            </button>

            {dropdownOpen && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, zIndex: 100,
                background: '#fff', border: '1px solid #ddd', borderRadius: 8,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: 200,
                maxHeight: 300, overflowY: 'auto', marginTop: 4
              }}>
                <label style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                  cursor: 'pointer', borderBottom: '1px solid #eee',
                  fontWeight: 600, fontSize: '0.85rem'
                }}>
                  <input
                    type="checkbox"
                    checked={platformFilters === null || platformFilters.size === platforms.length}
                    onChange={selectAllPlatforms}
                  />
                  Select All
                </label>
                {platforms.map(p => (
                  <label key={p} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px',
                    cursor: 'pointer', fontSize: '0.85rem'
                  }}>
                    <input
                      type="checkbox"
                      checked={platformFilters === null || platformFilters.has(p)}
                      onChange={() => togglePlatform(p)}
                    />
                    {p}
                  </label>
                ))}
              </div>
            )}
          </div>

          <span style={{ borderLeft: '1px solid #ddd', height: 24, margin: '0 0.25rem' }} />

          <div ref={statusDropRef} style={{ position: 'relative', display: 'inline-block' }}>
            <button
              onClick={() => setStatusDropOpen(!statusDropOpen)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: '#fff', border: '1px solid #ccc', borderRadius: 6,
                padding: '4px 12px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 500
              }}>
              <span>{statusFilter === 'All' ? `All Status (${conversations.length})` : `${statusFilter} (${getCount(statusFilter)})`}</span>
              <span style={{ fontSize: 10 }}>{statusDropOpen ? '\u25B2' : '\u25BC'}</span>
            </button>
            {statusDropOpen && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, zIndex: 100,
                background: '#fff', border: '1px solid #ddd', borderRadius: 8,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: 180,
                maxHeight: 280, overflowY: 'auto', marginTop: 4
              }}>
                {STATUS_TABS.map(s => {
                  const cnt = s === 'All' ? conversations.length : getCount(s)
                  const active = statusFilter === 'All' ? true : statusFilter === s
                  return (
                    <label key={s} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px',
                      cursor: 'pointer', fontSize: '0.82rem',
                      background: active && s !== 'All' ? '#e3f2fd' : 'transparent'
                    }}>
                      <input
                        type="checkbox"
                        checked={statusFilter === 'All' || statusFilter === s}
                        onChange={() => setStatusFilter(statusFilter === s ? 'All' : s)}
                      />
                      {s} ({cnt})
                    </label>
                  )
                })}
              </div>
            )}
          </div>

          <div ref={typeDropRef} style={{ position: 'relative', display: 'inline-block' }}>
            <button
              onClick={() => setTypeDropOpen(!typeDropOpen)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: '#fff', border: '1px solid #ccc', borderRadius: 6,
                padding: '4px 12px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 500
              }}>
              <span>{typeFilter === 'All' ? `All Types` : `${typeFilter} (${conversations.filter(c => c.conversationType === typeFilter).length})`}</span>
              <span style={{ fontSize: 10 }}>{typeDropOpen ? '\u25B2' : '\u25BC'}</span>
            </button>
            {typeDropOpen && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, zIndex: 100,
                background: '#fff', border: '1px solid #ddd', borderRadius: 8,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: 160,
                maxHeight: 280, overflowY: 'auto', marginTop: 4
              }}>
                {TYPE_OPTIONS.map(t => {
                  const cnt = t === 'All' ? conversations.length : conversations.filter(c => c.conversationType === t).length
                  return (
                    <label key={t} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px',
                      cursor: 'pointer', fontSize: '0.82rem',
                      background: typeFilter === t ? '#e3f2fd' : 'transparent'
                    }}>
                      <input
                        type="checkbox"
                        checked={typeFilter === 'All' || typeFilter === t}
                        onChange={() => setTypeFilter(typeFilter === t ? 'All' : t)}
                      />
                      {t} ({cnt})
                    </label>
                  )
                })}
              </div>
            )}
          </div>

          <span style={{ borderLeft: '1px solid #ddd', height: 24, margin: '0 0.25rem' }} />

          <div style={{ display: 'inline-flex', border: '1px solid #ccc', borderRadius: 6, overflow: 'hidden', flexShrink: 0, height: 28 }}>
            <button
              onClick={() => setViewMode('conversations')}
              style={{
                fontSize: '0.82rem', padding: '0 10px', border: 'none',
                background: viewMode === 'conversations' ? '#1976d2' : '#fff',
                color: viewMode === 'conversations' ? '#fff' : '#333',
                cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap'
              }}>Conversations</button>
            <button
              onClick={() => setViewMode('byPost')}
              style={{
                fontSize: '0.82rem', padding: '0 10px', border: 'none', borderLeft: '1px solid #ccc',
                background: viewMode === 'byPost' ? '#1976d2' : '#fff',
                color: viewMode === 'byPost' ? '#fff' : '#333',
                cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap'
              }}>By Post</button>
          </div>

          <span style={{ borderLeft: '1px solid #ddd', height: 24, margin: '0 0.25rem' }} />

          <span style={{ fontSize: '0.82rem', color: '#666', whiteSpace: 'nowrap' }}>Select between</span>
          <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setListPage(1) }}
            style={{ fontSize: '0.82rem', padding: '4px 6px', border: '1px solid #ccc', borderRadius: 6, width: 130, height: 28, boxSizing: 'border-box' }}
            title="From date" />
          <span style={{ fontSize: '0.82rem', color: '#999' }}>-</span>
          <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setListPage(1) }}
            style={{ fontSize: '0.82rem', padding: '4px 6px', border: '1px solid #ccc', borderRadius: 6, width: 130, height: 28, boxSizing: 'border-box' }}
            title="To date" />

          <span style={{ borderLeft: '1px solid #ddd', height: 24, margin: '0 0.25rem' }} />

          <span style={{ fontSize: '0.82rem', color: '#666', whiteSpace: 'nowrap' }}>Order by:</span>
          <select value={sortBy} onChange={e => setSortBy(e.target.value as 'newest' | 'reactions' | 'postLikes')}
            style={{ fontSize: '0.82rem', padding: '4px 8px', border: '1px solid #ccc', borderRadius: 6, height: 28 }}>
            <option value="newest">Newest</option>
            <option value="reactions">Comment Reactions</option>
            <option value="postLikes">Post Likes</option>
          </select>
        </div>
      </div>

      <div className={`smm-inbox-grid ${mobileShowDetail ? 'smm-inbox-mobile-detail' : ''}`}>
        {tiktokGateModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.4)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }} onClick={() => setTiktokGateModal(false)}>
            <div style={{
              background: '#fff', borderRadius: 12, padding: '24px 28px', maxWidth: 440,
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)', textAlign: 'center'
            }} onClick={e => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 12px', fontSize: '1.1rem', color: '#212121' }}>
                TikTok Business Account Required
              </h3>
              <p style={{ margin: '0 0 16px', fontSize: '0.9rem', color: '#555', lineHeight: 1.5 }}>
                TikTok Comment Management is available for Business Accounts only.
                Connect a Business Account (or convert your Creator account via TikTok settings)
                to reply, hide, delete and like comments directly from here.
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                <button onClick={() => setTiktokGateModal(false)}
                  style={{
                    padding: '8px 20px', borderRadius: 6, border: '1px solid #ccc',
                    background: '#f5f5f5', cursor: 'pointer', fontSize: '0.85rem'
                  }}>OK</button>
              </div>
            </div>
          </div>
        )}
        {showReauthModal && reauthConnections.length > 0 && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.45)', zIndex: 10000,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }} onClick={() => setShowReauthModal(false)}>
            <div style={{
              background: '#fff', borderRadius: 12, padding: '24px 28px', maxWidth: 480, width: '90%',
              boxShadow: '0 8px 32px rgba(0,0,0,0.25)'
            }} onClick={e => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 8px', fontSize: '1.1rem', color: '#d32f2f' }}>
                Reconnect Required
              </h3>
              <p style={{ margin: '0 0 16px', fontSize: '0.88rem', color: '#555', lineHeight: 1.5 }}>
                The following connections have expired or encountered an error and need to be reconnected to continue working.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                {reauthConnections.map(c => (
                  <div key={c.connectionID} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 12px', borderRadius: 8, background: '#fff3f3', border: '1px solid #ffcdd2'
                  }}>
                    <div>
                      <strong style={{ fontSize: '0.88rem' }}>{c.platformName}</strong>
                      <span style={{ fontSize: '0.8rem', color: '#777', marginLeft: 6 }}>{c.displayName}</span>
                      {c.lastTokenError && (
                        <div style={{ fontSize: '0.75rem', color: '#b71c1c', marginTop: 2 }}>{c.lastTokenError}</div>
                      )}
                    </div>
                    <button
                      onClick={() => { setShowReauthModal(false); navigate(`/connections?reconnect=${encodeURIComponent(c.platformName)}`) }}
                      style={{
                        padding: '5px 14px', borderRadius: 6, border: 'none', fontSize: '0.82rem',
                        background: '#1976d2', color: '#fff', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap'
                      }}>Reconnect</button>
                  </div>
                ))}
              </div>
              <div style={{ textAlign: 'right' }}>
                <button onClick={() => setShowReauthModal(false)} style={{
                  padding: '6px 18px', borderRadius: 6, border: '1px solid #ccc',
                  background: '#f5f5f5', cursor: 'pointer', fontSize: '0.82rem'
                }}>Dismiss</button>
              </div>
            </div>
          </div>
        )}
        <div className="smm-inbox-list">
          {loading && <p className="smm-hint">Loading...</p>}
          {!loading && filtered.length === 0 && (
            <p className="smm-empty">
              {conversations.length === 0
                ? 'No conversations yet. Comments from Facebook, Instagram, Threads, and Bluesky arrive automatically. Use "Sync" buttons above for YouTube, LinkedIn, and TikTok.'
                : 'No conversations match the selected filters.'}
            </p>
          )}
          {viewMode === 'conversations' && filtered.map(c => (
            <div key={c.conversationID}
              onClick={() => selectConversation(c.conversationID)}
              className={`smm-inbox-item ${selected?.conversationID === c.conversationID ? 'active' : ''}`}
              style={{
                borderLeft: `3px solid ${
                  c.platformName === 'Facebook' ? '#1877F2'
                  : c.platformName === 'Instagram' ? '#E1306C'
                  : c.platformName === 'Threads' ? '#000'
                  : c.platformName === 'X/Twitter' ? '#1DA1F2'
                  : c.platformName === 'YouTube' ? '#FF0000'
                  : c.platformName === 'Bluesky' ? '#0085ff'
                  : c.platformName === 'TikTok' ? '#00f2ea'
                  : c.platformName === 'LinkedIn' ? '#0A66C2'
                  : c.platformName === 'Telegram' ? '#0088cc'
                  : '#ccc'}`
              }}>
              <div className="smm-inbox-item-header">
                <span className="smm-inbox-participant">{c.participantName || 'Unknown'}</span>
                {c.status === 'New' && (
                  <span style={{
                    background: '#1976d2', color: '#fff', fontSize: '0.65rem',
                    padding: '1px 6px', borderRadius: 8, fontWeight: 600
                  }}>NEW</span>
                )}
              </div>
              <div className="smm-inbox-item-meta">
                <span className="smm-inbox-platform">{c.platformName}</span>
                <span className="smm-inbox-type">{c.conversationType}</span>
                {c.lastMessageAt && (
                  <span className="smm-inbox-time">
                    {new Date(c.lastMessageAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
            </div>
          ))}
          {viewMode === 'byPost' && (() => {
            const postGroups = new Map<string, Conversation[]>()
            for (const c of allFiltered) {
              const key = c.originalPostID || `no-post-${c.conversationID}`
              if (!postGroups.has(key)) postGroups.set(key, [])
              postGroups.get(key)!.push(c)
            }
            const sorted = [...postGroups.entries()].sort((a, b) => {
              if (sortBy === 'reactions') {
                const aLikes = a[1].reduce((sum, c2) => sum + (c2.totalLikes ?? 0), 0)
                const bLikes = b[1].reduce((sum, c2) => sum + (c2.totalLikes ?? 0), 0)
                return bLikes - aLikes
              }
              if (sortBy === 'postLikes') {
                const aPost = Math.max(...a[1].map(c2 => c2.postLikes ?? 0))
                const bPost = Math.max(...b[1].map(c2 => c2.postLikes ?? 0))
                return bPost - aPost
              }
              const aLatest = Math.max(...a[1].map(c2 => c2.lastMessageAt ? new Date(c2.lastMessageAt).getTime() : 0))
              const bLatest = Math.max(...b[1].map(c2 => c2.lastMessageAt ? new Date(c2.lastMessageAt).getTime() : 0))
              return bLatest - aLatest
            })
            return sorted.slice((listPage - 1) * listPageSize, listPage * listPageSize).map(([postId, convs]) => {
              const first = convs[0]
              const totalComments = convs.length
              const totalReactions = convs.reduce((sum, c2) => sum + (c2.totalLikes ?? 0), 0)
              const postEngagement = Math.max(...convs.map(c2 => c2.postLikes ?? 0))
              const latestDate = new Date(Math.max(...convs.map(c2 => c2.lastMessageAt ? new Date(c2.lastMessageAt).getTime() : 0)))
              const isDM = first.conversationType === 'DM'
              return (
                <div key={postId}
                  onClick={() => selectConversation(first.conversationID)}
                  style={{
                    borderBottom: '1px solid #eee', padding: '10px 12px', cursor: 'pointer',
                    background: selected?.conversationID === first.conversationID ? '#e3f2fd' : 'transparent',
                    borderLeft: `3px solid ${
                      first.platformName === 'Facebook' ? '#1877F2'
                      : first.platformName === 'Instagram' ? '#E1306C'
                      : first.platformName === 'YouTube' ? '#FF0000'
                      : first.platformName === 'LinkedIn' ? '#0A66C2'
                      : first.platformName === 'Bluesky' ? '#0085ff'
                      : first.platformName === 'X/Twitter' ? '#1DA1F2'
                      : first.platformName === 'TikTok' ? '#00f2ea'
                      : '#ccc'}`
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                    <span style={{
                      fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase',
                      color: '#fff', padding: '1px 6px', borderRadius: 4,
                      background: first.platformName === 'YouTube' ? '#FF0000'
                        : first.platformName === 'Facebook' ? '#1877F2'
                        : first.platformName === 'Instagram' ? '#E1306C'
                        : first.platformName === 'LinkedIn' ? '#0A66C2'
                        : first.platformName === 'Bluesky' ? '#0085ff'
                        : first.platformName === 'X/Twitter' ? '#1DA1F2'
                        : '#666'
                    }}>
                      {first.platformName}
                    </span>
                    <span style={{ fontSize: '0.72rem', color: '#666' }}>
                      {isDM ? 'DM' : 'Comment'}
                    </span>
                    <span style={{ fontSize: '0.7rem', color: '#999', marginLeft: 'auto' }}>
                      {latestDate.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  {isDM ? (
                    <p style={{ margin: '0 0 4px', fontSize: '0.82rem', fontWeight: 500, color: '#333' }}>
                      DM - {first.participantName || 'Unknown'}
                    </p>
                  ) : (
                    <p style={{ margin: '0 0 4px', fontSize: '0.82rem', color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {first.originalPostPreview || '(Post)'}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: 10, fontSize: '0.72rem', color: '#777' }}>
                    <span>{totalComments} comment{totalComments !== 1 ? 's' : ''}</span>
                    {totalReactions > 0 && <span>&#128077; {totalReactions}</span>}
                    {postEngagement > 0 && <span>&#10084; {postEngagement}</span>}
                    {convs.some(c => c.status === 'New') && (
                      <span style={{ color: '#1976d2', fontWeight: 600 }}>NEW</span>
                    )}
                  </div>
                </div>
              )
            })
          })()}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderTop: '1px solid #eee', fontSize: '0.78rem', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <label style={{ color: '#666' }}>Items per page</label>
              <select value={listPageSize} onChange={e => { setListPageSize(+e.target.value); setListPage(1) }}
                style={{ padding: '2px 6px', fontSize: '0.78rem', border: '1px solid #ddd', borderRadius: 4 }}>
                {[25, 50, 100, 200].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button disabled={listPage <= 1} onClick={() => setListPage(p => p - 1)}
                style={{ padding: '2px 8px', border: '1px solid #ddd', borderRadius: 4, cursor: listPage <= 1 ? 'default' : 'pointer', opacity: listPage <= 1 ? 0.4 : 1 }}>&laquo;</button>
              <span style={{ color: '#666' }}>{listPage}/{totalPages} ({allFiltered.length})</span>
              <button disabled={listPage >= totalPages} onClick={() => setListPage(p => p + 1)}
                style={{ padding: '2px 8px', border: '1px solid #ddd', borderRadius: 4, cursor: listPage >= totalPages ? 'default' : 'pointer', opacity: listPage >= totalPages ? 0.4 : 1 }}>&raquo;</button>
            </div>
          </div>
        </div>

        <div className="smm-inbox-detail">
          <button className="smm-inbox-back-btn" onClick={() => setMobileShowDetail(false)}
            style={{ display: 'none', padding: '8px 12px', fontSize: '0.85rem', border: '1px solid #d1d5db', borderRadius: 6, background: '#f8fafc', cursor: 'pointer', marginBottom: 8, width: '100%', textAlign: 'left' }}>
            &larr; Back to conversations
          </button>
          {!selected && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', marginBottom: 8 }}>&#128172;</div>
                <p>Select a conversation to view details</p>
              </div>
            </div>
          )}
          {selected && (
            <>
              <div style={{
                padding: '12px 16px', borderBottom: '1px solid #e0e0e0',
                background: '#fafafa', display: 'flex', justifyContent: 'space-between',
                alignItems: 'flex-start', gap: 12
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{
                      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
                      fontSize: '0.72rem', fontWeight: 600, color: '#fff',
                      background: selected.platformName === 'Facebook' ? '#1877F2'
                        : selected.platformName === 'Instagram' ? '#E1306C'
                        : selected.platformName === 'Threads' ? '#000'
                        : selected.platformName === 'X/Twitter' ? '#1DA1F2'
                        : selected.platformName === 'YouTube' ? '#FF0000'
                        : selected.platformName === 'Bluesky' ? '#0085ff'
                        : '#666'
                    }}>{selected.platformName}</span>
                    <span style={{ fontSize: '0.78rem', color: '#666', textTransform: 'capitalize' }}>
                      {selected.conversationType}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: '#999' }}>
                      on {selected.connectionName}
                    </span>
                  </div>
                  <h3 style={{ margin: 0, fontSize: '1rem' }}>{selected.participantName || 'Unknown'}</h3>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                  <select value={selected.status}
                    onChange={e => handleStatusChange(e.target.value)}
                    className="smm-select" style={{ fontSize: '0.82rem', padding: '3px 8px' }}>
                    {['New', 'Open', 'Pending', 'Resolved'].map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  {selected.conversationType === 'Comment' && selected.originalPostID && selected.platformName === 'Instagram' && (
                    <button onClick={() => handleDisableComments(true)} className="smm-btn smm-btn-sm"
                      style={{ fontSize: '0.75rem', padding: '3px 8px', background: '#fff3e0', border: '1px solid #ffe0b2' }}>
                      Disable Comments
                    </button>
                  )}
                  <button onClick={handleArchive} className="smm-btn smm-btn-sm"
                    style={{ fontSize: '0.75rem', padding: '3px 8px' }}>
                    {selected.isArchived ? 'Unarchive' : 'Archive'}
                  </button>
                </div>
              </div>

              {((selected.labels?.length ?? 0) > 0 || labels.length > 0) && (
                <div style={{ padding: '6px 16px', borderBottom: '1px solid #eee', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  {selected.labels?.map(l => (
                    <span key={l.labelID} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '2px 8px', borderRadius: 12, fontSize: '0.72rem',
                      border: `1px solid ${l.color || '#6c757d'}`, background: `${l.color || '#6c757d'}15`
                    }}>
                      {l.name}
                      <button onClick={() => handleRemoveLabel(l.labelID)}
                        style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.7rem', padding: 0, color: '#999' }}>x</button>
                    </span>
                  ))}
                  {labels.length > 0 && (
                    <select onChange={e => { if (e.target.value) handleAddLabel(Number(e.target.value)); e.target.value = '' }}
                      className="smm-select" defaultValue="" style={{ fontSize: '0.72rem', padding: '2px 6px', borderRadius: 12 }}>
                      <option value="" disabled>+ Label</option>
                      {labels.filter(l => !selected.labels?.some(sl => sl.labelID === l.labelID))
                        .map(l => <option key={l.labelID} value={l.labelID}>{l.name}</option>)}
                    </select>
                  )}
                </div>
              )}

              {(selected.originalPostUrl || selected.originalPostID || origPost) && (
                <div
                  onClick={() => setOrigPostModal(true)}
                  style={{
                    margin: '8px 16px', padding: '10px 14px', borderRadius: 8,
                    background: '#f0f4ff', border: '1px solid #bbdefb',
                    fontSize: '0.82rem', cursor: 'pointer',
                    display: 'flex', gap: 10, alignItems: 'flex-start'
                  }}
                >
                  {(origPost?.imageUrl || (selected.platformName === 'YouTube' && selected.originalPostUrl)) && (() => {
                    let ytThumb = origPost?.imageUrl
                    if (!ytThumb && selected.platformName === 'YouTube' && selected.originalPostUrl) {
                      const url = selected.originalPostUrl
                      let vid = url.match(/[?&]v=([^&]+)/)?.[1]
                      if (!vid) vid = url.match(/\/shorts\/([^/?]+)/)?.[1]
                      if (vid) ytThumb = `https://img.youtube.com/vi/${vid}/mqdefault.jpg`
                    }
                    return ytThumb ? (
                      <img src={ytThumb} alt="Post"
                        style={{ width: 56, height: 56, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
                    ) : null
                  })()}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, color: '#666', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      <span>Original Post</span>
                      {origPostLoading && <span style={{ color: '#999', fontWeight: 400 }}>loading...</span>}
                    </div>
                    {origPost?.preview ? (
                      <p style={{ margin: 0, color: '#333', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const }}>
                        {origPost.preview}
                      </p>
                    ) : !origPostLoading ? (
                      <span style={{ color: '#999', fontStyle: 'italic', fontSize: '0.75rem' }}>
                        Click to view original post details
                      </span>
                    ) : null}
                  </div>
                </div>
              )}

              {origPostModal && origPost && (
                <div onClick={() => setOrigPostModal(false)}
                  style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
                  <div onClick={e => e.stopPropagation()}
                    style={{ background: '#fff', borderRadius: 12, maxWidth: 560, width: '90%', maxHeight: '80vh', overflow: 'auto', padding: 24, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                      <h3 style={{ margin: 0, fontSize: '1rem' }}>Original Post</h3>
                      <button onClick={() => setOrigPostModal(false)}
                        style={{ border: 'none', background: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#666' }}>x</button>
                    </div>
                    {selected.platformName === 'YouTube' && origPost.url && (() => {
                      let vid = origPost.url.match(/[?&]v=([^&]+)/)?.[1]
                      if (!vid) vid = origPost.url.match(/\/shorts\/([^/?]+)/)?.[1]
                      if (!vid) return null
                      return (
                        <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, marginBottom: 12 }}>
                          <iframe
                            src={`https://www.youtube.com/embed/${vid}`}
                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius: 8, border: 'none' }}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      )
                    })()}
                    {selected.platformName !== 'YouTube' && origPost.imageUrl && (
                      <img src={origPost.imageUrl} alt="Post" style={{ width: '100%', borderRadius: 8, marginBottom: 12 }} />
                    )}
                    {origPost.preview && (
                      <p style={{ margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap', fontSize: '0.95rem' }}>{origPost.preview}</p>
                    )}
                    <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {origPost.url && (
                        <a href={origPost.url} target="_blank" rel="noopener noreferrer"
                          style={{ padding: '6px 16px', background: '#1976d2', color: '#fff', borderRadius: 6, textDecoration: 'none', fontSize: '0.85rem' }}>
                          View on {selected.platformName}
                        </a>
                      )}
                      {origPost.postId && selected.platformName !== 'Threads' && (
                        <button onClick={async () => {
                          const newCaption = prompt('Edit caption:', origPost.preview || '')
                          if (newCaption === null) return
                          try {
                            await api.put(`/workspaces/${wsId}/inbox/posts/${encodeURIComponent(origPost.postId!)}/caption?connectionId=${selected.connectionID}`, { caption: newCaption })
                            setOrigPost({ ...origPost, preview: newCaption })
                            alert('Caption updated')
                          } catch (err) {
                            const msg = err instanceof Error ? err.message : 'Edit failed'
                            console.error('[SMM] edit caption failed', err)
                            alert(msg)
                          }
                        }}
                          style={{ padding: '6px 16px', background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem', color: '#2e7d32' }}>
                          Edit Caption
                        </button>
                      )}
                      <button onClick={() => setOrigPostModal(false)}
                        style={{ padding: '6px 16px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem' }}>
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="smm-inbox-messages" style={{ padding: '12px 16px', flex: 1, overflowY: 'auto' }}>
                {(() => {
                  const msgs = (selected.messages ?? []).filter(m => !m.platformMessageID || !deletedMsgIds.has(m.platformMessageID))
                  const total = msgs.length
                  const visible = msgs.slice(0, msgPage * MSG_PAGE_SIZE)
                  return <>
                    {total > MSG_PAGE_SIZE && visible.length < total && (
                      <div style={{ textAlign: 'center', marginBottom: 12 }}>
                        <button onClick={() => setMsgPage(p => p + 1)}
                          className="smm-btn smm-btn-sm" style={{ fontSize: '0.75rem' }}>
                          Show older ({total - visible.length} more)
                        </button>
                      </div>
                    )}
                    {visible.map((m, _mi) => {
                      const isOut = m.direction === 'Outbound'
                      const isNote = m.isInternal
                      const isNested = !!(m as any).parentMessageID && (m as any).parentMessageID > 0
                      return (
                        <div key={m.messageID} style={{
                          marginBottom: 12,
                          marginLeft: isNested ? 28 : 0,
                          display: 'flex', flexDirection: 'column',
                          alignItems: isOut ? 'flex-end' : 'flex-start'
                        }}>
                          {isNested && (
                            <div style={{ fontSize: '0.68rem', color: '#999', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span style={{ borderLeft: '2px solid #bbb', height: 10, display: 'inline-block' }} />
                              reply
                            </div>
                          )}
                          <div style={{
                            maxWidth: '80%', borderRadius: 12, padding: '8px 14px',
                            background: isNote ? '#fff8e1' : isOut ? '#e3f2fd' : '#fff',
                            border: `1px solid ${isNote ? '#ffe082' : isOut ? '#bbdefb' : '#e0e0e0'}`,
                            borderLeft: !isOut && !isNote ? '3px solid #1976d2' : undefined,
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 4 }}>
                              <strong style={{ fontSize: '0.82rem', color: isNote ? '#f57f17' : isOut ? '#1565c0' : '#333' }}>
                                {isNote ? 'Note' : ''} {m.senderName || (isOut ? 'You' : selected.participantName)}
                              </strong>
                              <span style={{ fontSize: '0.7rem', color: '#999', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                                {new Date(m.sentAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                {(m as any).isEdited && <span title="This message was edited" style={{ fontStyle: 'italic', color: '#888' }}>(edited)</span>}
                              </span>
                            </div>
                            {editingMsgId && editingMsgId === (m.platformMessageID || `msg:${m.messageID}`) ? (
                              <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
                                <input type="text" value={editText}
                                  onChange={e => setEditText(e.target.value)}
                                  onKeyDown={e => { if (e.key === 'Enter') handleEditMessage(m.platformMessageID || `msg:${m.messageID}`) }}
                                  style={{ flex: 1, fontSize: '0.85rem', padding: '4px 8px', border: '1px solid #90caf9', borderRadius: 4 }}
                                  autoFocus />
                                <button onClick={() => handleEditMessage(m.platformMessageID || `msg:${m.messageID}`)}
                                  style={{ fontSize: '0.75rem', padding: '3px 8px', background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 4, cursor: 'pointer' }}>Save</button>
                                <button onClick={() => { setEditingMsgId(null); setEditText('') }}
                                  style={{ fontSize: '0.75rem', padding: '3px 8px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 4, cursor: 'pointer' }}>Cancel</button>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                                <p style={{ margin: 0, fontSize: '0.88rem', lineHeight: 1.35, whiteSpace: 'pre-wrap', flex: 1 }}>{(m.content || '').trimEnd()}</p>
                                {isOut && m.content && (() => {
                                  const msgId = m.platformMessageID || `msg:${m.messageID}`
                                  const isLocal = msgId.startsWith('msg:')
                                  const editableCommentPlatforms = new Set(['Facebook', 'Instagram', 'Threads', 'YouTube', 'LinkedIn'])
                                  const editableDMPlatforms = new Set(['Facebook', 'Instagram'])
                                  const isEditablePlatform = selected.conversationType === 'Comment'
                                    ? editableCommentPlatforms.has(selected.platformName || '')
                                    : selected.conversationType === 'DM'
                                      ? editableDMPlatforms.has(selected.platformName || '')
                                      : false
                                  const canEdit = isLocal ? isEditablePlatform : isEditablePlatform
                                  if (!canEdit) return null
                                  return (
                                    <button onClick={() => { setEditingMsgId(msgId); setEditText(m.content || '') }}
                                      title="Edit message"
                                      style={{ fontSize: '0.7rem', background: 'none', border: 'none', cursor: 'pointer', color: '#999', padding: '2px', flexShrink: 0, marginTop: 2 }}>
                                      &#9998;
                                    </button>
                                  )
                                })()}
                              </div>
                            )}
                            {m.mediaJson && (() => {
                              try {
                                const attachments: Array<{ type?: string; url?: string; size?: number }> = JSON.parse(m.mediaJson)
                                if (!Array.isArray(attachments) || attachments.length === 0) return null
                                return (
                                  <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    {attachments.map((att, i) => {
                                      if (att.type === 'image')
                                        return <img key={i} src={att.url} alt="attachment" style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8 }} />
                                      if (att.type === 'video')
                                        return <video key={i} src={att.url} controls style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8 }} />
                                      if (att.type === 'audio')
                                        return <audio key={i} src={att.url} controls style={{ width: '100%' }} />
                                      return (
                                        <a key={i} href={att.url} target="_blank" rel="noopener noreferrer"
                                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: '#f5f5f5', borderRadius: 6, fontSize: '0.8rem', color: '#1976d2', textDecoration: 'none', border: '1px solid #e0e0e0' }}>
                                          {att.type === 'file' ? '📎' : '📄'} {att.type ?? 'file'}
                                          {att.size ? ` (${(att.size / 1024 / 1024).toFixed(1)}MB)` : ''}
                                          <span style={{ fontSize: '0.7rem', color: '#999', marginLeft: 4 }}>download at own risk</span>
                                        </a>
                                      )
                                    })}
                                  </div>
                                )
                              } catch { return null }
                            })()}
                            {m.costUSD != null && m.costUSD > 0 && (
                              <span style={{ fontSize: '0.65rem', color: '#e65100', marginTop: 3, display: 'inline-block' }}>${m.costUSD.toFixed(4)}</span>
                            )}
                            {!isOut && m.likeCount != null && m.likeCount > 0 && (
                              <span style={{ fontSize: '0.72rem', color: '#e91e63', marginTop: 3, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                &#10084; {m.likeCount}
                              </span>
                            )}
                            {!isOut && !isNote && m.platformMessageID && selected.conversationType === 'Comment' && !deletedMsgIds.has(m.platformMessageID) && (
                              <div style={{ marginTop: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                {selected.platformName === 'YouTube' ? (
                                  <>
                                    {m.moderationStatusID === 2 || hiddenMsgIds.has(m.platformMessageID) ? (
                                      <button onClick={() => handleModerate(m.platformMessageID!, 'publish')}
                                        style={{ fontSize: '0.68rem', padding: '2px 6px', background: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: 4, cursor: 'pointer', color: '#2e7d32' }}>
                                        Publish
                                      </button>
                                    ) : (
                                      <button onClick={() => handleModerate(m.platformMessageID!, 'flag')}
                                        style={{ fontSize: '0.68rem', padding: '2px 6px', background: '#fff3e0', border: '1px solid #ffe0b2', borderRadius: 4, cursor: 'pointer', color: '#e65100' }}>
                                        Flag
                                      </button>
                                    )}
                                    <button onClick={() => handleModerate(m.platformMessageID!, 'reject')}
                                      style={{ fontSize: '0.68rem', padding: '2px 6px', background: '#fce4ec', border: '1px solid #f8bbd0', borderRadius: 4, cursor: 'pointer', color: '#ad1457' }}>
                                      Reject
                                    </button>
                                    <button onClick={() => handleModerate(m.platformMessageID!, 'delete')}
                                      style={{ fontSize: '0.68rem', padding: '2px 6px', background: '#ffebee', border: '1px solid #ffcdd2', borderRadius: 4, cursor: 'pointer', color: '#c62828' }}>
                                      Delete
                                    </button>
                                  </>
                                ) : selected.platformName === 'Bluesky' ? (
                                  <>
                                    <button onClick={() => handleDeleteComment(m.platformMessageID!)}
                                      style={{ fontSize: '0.68rem', padding: '2px 6px', background: '#ffebee', border: '1px solid #ffcdd2', borderRadius: 4, cursor: 'pointer', color: '#c62828' }}>
                                      Delete
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    {hiddenMsgIds.has(m.platformMessageID) ? (
                                      <button onClick={() => handleHideComment(m.platformMessageID!)}
                                        style={{ fontSize: '0.68rem', padding: '2px 6px', background: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: 4, cursor: 'pointer', color: '#2e7d32' }}>
                                        Unhide
                                      </button>
                                    ) : (
                                      <button onClick={() => handleHideComment(m.platformMessageID!)}
                                        style={{ fontSize: '0.68rem', padding: '2px 6px', background: '#fff3e0', border: '1px solid #ffe0b2', borderRadius: 4, cursor: 'pointer', color: '#e65100' }}>
                                        Hide
                                      </button>
                                    )}
                                    {selected.platformName !== 'Threads' && (
                                      <button onClick={() => handleDeleteComment(m.platformMessageID!)}
                                        style={{ fontSize: '0.68rem', padding: '2px 6px', background: '#ffebee', border: '1px solid #ffcdd2', borderRadius: 4, cursor: 'pointer', color: '#c62828' }}>
                                        Delete
                                      </button>
                                    )}
                                  </>
                                )}
                                {selected.platformName === 'TikTok' && (
                                  <button onClick={() => handleReactToMessage(m.platformMessageID!, 'like')}
                                    style={{ fontSize: '0.68rem', padding: '2px 6px', background: '#fce4ec', border: '1px solid #f8bbd0', borderRadius: 4, cursor: 'pointer', color: '#c62828' }}>
                                    Like
                                  </button>
                                )}
                              </div>
                            )}
                            {!isOut && !isNote && m.platformMessageID && selected.conversationType === 'DM' && selected.platformName !== 'X/Twitter' && selected.platformName !== 'Bluesky' && (
                              <div style={{ marginTop: 4, position: 'relative' }}>
                                <button onClick={() => setReactPopupMsgId(reactPopupMsgId === m.platformMessageID ? null : m.platformMessageID!)}
                                  style={{ fontSize: '0.82rem', padding: '2px 6px', background: '#f3e5f5', border: '1px solid #e1bee7', borderRadius: 4, cursor: 'pointer', color: '#6a1b9a' }}>
                                  ☺
                                </button>
                                {reactPopupMsgId === m.platformMessageID && (
                                  <div style={{
                                    position: 'absolute', bottom: '100%', left: 0, marginBottom: 4,
                                    display: 'flex', gap: 2, background: '#fff', border: '1px solid #e0e0e0',
                                    borderRadius: 20, padding: '4px 8px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 100
                                  }}>
                                    {REACTION_EMOJIS.map(emoji => (
                                      <button key={emoji}
                                        onClick={() => handleReactToMessage(m.platformMessageID!, emoji)}
                                        style={{ fontSize: '1.2rem', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', borderRadius: '50%', transition: 'transform 0.1s' }}
                                        onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.3)')}
                                        onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}>
                                        {emoji}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          {(() => {
                            const dbReactions: Array<{ sender: string; emoji: string }> = (() => {
                              try {
                                if (!m.reactionsJson) return []
                                const parsed = JSON.parse(m.reactionsJson)
                                return Array.isArray(parsed) ? parsed : []
                              } catch { return [] }
                            })()
                            const freshReaction = m.platformMessageID ? sentReactions[m.platformMessageID] : undefined
                            let allReactions: Array<{ sender: string; emoji: string }>
                            if (freshReaction) {
                              allReactions = [...dbReactions.filter(r => r.sender !== 'You'), { sender: 'You', emoji: freshReaction }]
                            } else if (m.platformMessageID && sentReactions[m.platformMessageID] === '') {
                              allReactions = dbReactions.filter(r => r.sender !== 'You')
                            } else {
                              allReactions = dbReactions
                            }
                            if (allReactions.length === 0) return null
                            return (
                              <div style={{
                                marginTop: -6,
                                display: 'flex',
                                gap: 3,
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                justifyContent: isOut ? 'flex-end' : 'flex-start',
                                maxWidth: '80%',
                                position: 'relative',
                                zIndex: 1
                              }}>
                                {allReactions.map((r, i) => (
                                  <span key={i} style={{
                                    fontSize: '0.82rem', lineHeight: 1,
                                    background: r.sender === 'You' ? '#e8f5e9' : '#f0f0f0',
                                    borderRadius: 10, padding: '2px 6px',
                                    border: `1px solid ${r.sender === 'You' ? '#a5d6a7' : '#e0e0e0'}`,
                                    display: 'inline-flex', alignItems: 'center', gap: 3,
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.08)'
                                  }}>
                                    <span>{r.emoji}</span>
                                    {r.sender === 'You' && m.platformMessageID && (
                                      <button onClick={() => handleUnreactToMessage(m.platformMessageID!)}
                                        style={{ fontSize: '0.6rem', background: 'transparent', border: 'none', cursor: 'pointer', color: '#666', lineHeight: 1, padding: 0 }}
                                        title="Remove your reaction">x</button>
                                    )}
                                  </span>
                                ))}
                              </div>
                            )
                          })()}
                        </div>
                      )
                    })}
                    <div ref={messagesEndRef} />
                  </>
                })()}
              </div>

              <div style={{ borderTop: '1px solid #e0e0e0', padding: '10px 16px', background: '#fafafa' }}>
                {replyError && (
                  <div style={{ background: '#ffebee', color: '#c62828', padding: '6px 12px', borderRadius: 6, fontSize: '0.8rem', marginBottom: 8 }}>
                    Reply failed: {replyError}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <textarea className="smm-textarea" rows={2} value={replyText}
                    onChange={e => { setReplyText(e.target.value); setReplyError(null) }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.ctrlKey && !e.shiftKey) {
                        e.preventDefault()
                        if (replyText.trim() && !replying) handleReply()
                      }
                    }}
                    placeholder="Type a reply... (Enter to send, Ctrl+Enter for new line)"
                    style={{ flex: 1, resize: 'vertical', fontSize: '0.88rem' }} />
                  <button onClick={handleReply} disabled={!replyText.trim() || replying}
                    className="smm-btn smm-btn-primary"
                    style={{ alignSelf: 'flex-end', padding: '6px 20px' }}>
                    {replying ? 'Sending...' : 'Reply'}
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="smm-input" value={noteText}
                    onChange={e => setNoteText(e.target.value)}
                    placeholder="Internal note (team only)..."
                    style={{ flex: 1, fontSize: '0.82rem', padding: '4px 8px' }} />
                  <button onClick={handleNote} disabled={!noteText.trim()}
                    className="smm-btn smm-btn-sm" style={{ fontSize: '0.75rem' }}>Add Note</button>
                </div>
              </div>
            </>
          )}
          {selected && (
            <button className="smm-inbox-back-btn" onClick={() => setMobileShowDetail(false)}
              style={{ display: 'none', padding: '8px 12px', fontSize: '0.85rem', border: '1px solid #d1d5db', borderRadius: 6, background: '#f8fafc', cursor: 'pointer', marginTop: 8, width: '100%', textAlign: 'left' }}>
              &larr; Back to conversations
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
